/**
 * The visible loading state the AEM launcher shows while the Angular bundles load.
 *
 * CLS - height reservation is NOT this file's job. It lives in the `rbmaz-root` class (launcher.ts
 * adds it to `<app-root>`) plus launch.css, mirroring az-bunfinder-spa: a `min-height` with
 * `!important`, applied unconditionally and never removed, on the one real element AEM gives us.
 * That is more robust than reserving space in a separate placeholder div here, because `app-root`
 * is never `display: none` either (a hidden element has no box, so a min-height on it would be
 * inert exactly when it matters), and because a class survives host-page CSS specificity fights
 * that a plain injected `<style>` block does not.
 *
 * This file only renders the *visible* loading state - logo and spinner - as a child of `app-root`,
 * centred in the first viewport so it sits within the space launch.css already reserved.
 *
 * LCP - the old logo was an inline `<svg>`, and inline SVG is not an LCP candidate in Chrome. It is
 * an `<img>` now, which is a candidate. Chrome scores an image as `min(displayed area, intrinsic
 * area)`, so the intrinsic 900x260 = 234 000 px² has to beat the `<h1>`'s rendered area at every
 * breakpoint - measured on RBMAZ: 44 100 px² (390 px), 96 288 px² (768 px), 150 960 px² (1280 px).
 * It does, comfortably, at every breakpoint. Chrome also drops images below 0.05 bits per pixel as
 * low-entropy placeholders; the encoded SVG is ~3.4 kB over 234 000 px² ~= 0.12 bpp, above that.
 *
 * Centred layout - the visible loading state sits in a wrapper one viewport tall, so logo and
 * spinner are centred on the first screen while the reserved height below stays empty. An element
 * outside the initial viewport is not an LCP candidate at all.
 */

const LOGO_PATH =
    "m4.85797553 25.1549776 1.43960637-4.8553068h5.6894456l1.3480457 4.8553068h5.2715453l-5.2378287-18.52226952c-.3009748-1.09370601-.9586034-1.59973978-2.0997095-1.59973978h-7.45724761v1.74930641h.62731451c.77857526 0 1.1998782.3059884 1.1998782.90705931 0 .3935472-.08753945.72415178-.39655681 1.62622554l-5.24246859 15.83941804zm6.41110477-8.1476423h-4.22230918l2.24725834-7.38890331zm13.4083837 8.1476423h-4.699291v-17.24939516c0-.79114516-.3281956-1.12330773-1.1058429-1.12330773h-.3000468v-1.74930641h4.6105141c.9313827 0 1.4946666.57396195 1.4946666 1.54178271zm7.9939294 0h-4.7023842v-17.24939516c0-.79114516-.3291236-1.12330773-1.1114108-1.12330773h-.293551v-1.74930641h4.6092769c.9267427 0 1.4980691.57396195 1.4980691 1.54178271zm8.1111642 0h-4.6757821v-12.2192823c0-.7855364-.3285049-1.1454312-1.1070802-1.1454312h-.2675676v-1.7162771h4.5498862c.9292173 0 1.5005437.5711576 1.5005437 1.536174zm.0878488-18.28576728c0 1.36510712-.9910827 2.17339013-2.6942354 2.17339013-1.675932 0-2.6998033-.80828301-2.6998033-2.17339013 0-1.34921567 1.0238713-2.16560021 2.6998033-2.16560021 1.7031527 0 2.6942354.81638454 2.6942354 2.16560021zm3.8820498 7.52102048-1.0405749-2.8984767c1.3990846-.6961081 2.000725-.9382191 3.1746197-1.2065042 1.1649243-.2701547 2.3620186-.4275113 3.5875709-.4275113 3.8071928 0 5.1261617 1.3333242 5.1261617 5.1625416v7.1838724c0 .9615888.298191 1.3267806 1.1055336 1.3267806h.2109608v1.6240444h-4.2813906c-.9313826 0-1.4074364-.4801712-1.4074364-1.4140279v-.2100165c-.961078.7543767-1.350211 1.0014733-2.0069115 1.2953094-.7507359.2991333-1.5908671.47799-2.5191565.47799-2.5445213 0-3.7703829-1.3856725-3.7703829-4.2514315 0-2.2603258.8914795-3.7298179 2.7811562-4.4932309.5679238-.2433574 1.1463646-.4222142 1.7690393-.5702228 1.0807874-.2399298 1.6790252-.3633223 3.5949946-.6683759v-.336525c0-1.3193023-.449761-1.772053-1.7115045-1.772053-1.3449524 0-3.052745.4237722-4.6126795 1.1778373zm6.324184 3.5210478v2.9318176c-.7819778.8151382-1.7359413 1.3034109-2.5426653 1.3034109-.8432245 0-1.1386315-.4175402-1.1386315-1.5473915 0-1.1164526.234779-1.6240443 1.0749102-1.9527793.6273145-.2745171 1.4368225-.5147585 2.6063866-.7350577zm12.6595038 7.243699h-4.6699049v-12.2192823c0-.7855364-.3334542-1.1454312-1.1117201-1.1454312h-.2694235v-1.7162771h4.4363632c.9561288 0 1.4433183.479548 1.4433183 1.4187019v.6016941c1.7285175-1.6290299 3.0153165-2.20018753 4.8434372-2.20018753 1.2323575 0 2.1903422.35709033 2.8201313 1.05631433.7513545.8092178.9236495 1.5632829.9236495 3.9504287v10.2540391h-4.6708329v-9.5560615c0-1.5168549-.2366349-1.9113369-1.2283362-1.9113369-.9295267 0-1.7343947.394482-2.5166819 1.2706932zm22.2786356-3.1664503v3.1664503h-11.768643v-3.1041308l6.2898488-8.9010843h-3.3589785c-.7442399 0-1.1030589.3324742-1.1030589 1.0248431v.2689083h-1.735632v-2.7401854c0-1.1198801.5085331-1.6312111 1.6174692-1.6312111h9.7604941v3.112544l-6.2307673 8.8038659zm37.6413454-5.8835774c0 8.8446852-6.560201 15.4452477-15.333943 15.4452477-8.7783814 0-15.337035-6.6005625-15.337035-15.4452477 0-8.83377926 6.5586536-15.4492984 15.337035-15.4492984 8.773742 0 15.333943 6.61551914 15.333943 15.4492984zm-2.605459 0c0 7.5126073-5.361559 12.9742822-12.728484 12.9742822-7.367852 0-12.7303391-5.4616749-12.7303391-12.9742822 0-7.51447691 5.3624871-12.9758402 12.7303391-12.9758402 7.366925 0 12.728484 5.49563897 12.728484 12.9758402zm-16.89233 8.6655168v-15.09127326h-2.606387c-1.289273 0-1.7950223.48048276-1.7950223 1.81349546v13.2777778zm8.330786 0h4.399863v-13.2777778c0-1.3330127-.510698-1.81349546-1.800281-1.81349546h-2.599582zm-1.954016 0v-17.04903817c0-1.35825199-.478529-1.81536501-1.789146-1.81536501h-3.960928v1.903547h.236635c.90478 0 1.079859.20503093 1.079859 1.3018529v15.65900328z";

/**
 * The logo centred on white, 900x260 intrinsic. See the LCP note above.
 *
 * `#004a93` is the blue of assets/images/allianz-logo.svg, the logo the app's own header renders,
 * so the placeholder and the loaded page show the same mark in the same colour. The white `<rect>`
 * looks redundant against a white page but is not: it gives the `<img>` its opaque painted area,
 * which is what Chrome measures for LCP.
 */
const STAGE_IMAGE =
    '<svg xmlns="http://www.w3.org/2000/svg" width="900" height="260" viewBox="0 0 900 260">' +
    '<rect width="900" height="260" fill="#ffffff"/>' +
    '<g transform="translate(326 98) scale(2)">' +
    `<path fill="#004a93" fill-rule="evenodd" d="${LOGO_PATH}"/>` +
    "</g></svg>";

export const LOADING_ROOT_CLASS = "rbmaz-loading";

const STYLE_ID = "rbmaz-loading-styles";

const STYLES = `
.${LOADING_ROOT_CLASS} {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    /* svh keeps mobile browsers from overflowing by the height of a retracting URL bar. vh first
       so a browser without svh still gets a full screen rather than no min-height at all. */
    min-height: 100vh;
    min-height: 100svh;
}

.${LOADING_ROOT_CLASS}__stage {
    display: block;
    width: 100%;
    height: 280px;
    /* cover, not contain: it fills the element box, which keeps the painted area - and so the LCP
       score - at its largest. */
    object-fit: cover;
    background-color: #ffffff;
}

.${LOADING_ROOT_CLASS}__ring {
    box-sizing: border-box;
    width: 22px;
    height: 22px;
    margin-top: 72px;
    border: 2px solid #ececec;
    border-top-color: #003781;
    border-radius: 50%;
    animation: rbmaz-loading-spin .75s linear infinite;
}

@keyframes rbmaz-loading-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
    .${LOADING_ROOT_CLASS}__ring { animation: none; }
}
`;

let placeholder: HTMLElement | null = null;

const injectStyles = (): void => {
    if (document.getElementById(STYLE_ID)) {
        return;
    }

    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = STYLES;
    document.head.appendChild(style);
};

/**
 * Appends the visible loading state as a child of the embed host. `host` (`<app-root>`) already
 * has its height reserved unconditionally via the `rbmaz-root` class (see launch.css), so this
 * only needs to centre itself within the first viewport of that reserved space.
 */
export const startLoadingScreen = (host: HTMLElement): void => {
    if (placeholder) {
        return;
    }

    injectStyles();

    placeholder = document.createElement("div");
    placeholder.className = LOADING_ROOT_CLASS;

    const stage = document.createElement("img");
    stage.className = `${LOADING_ROOT_CLASS}__stage`;
    stage.src = `data:image/svg+xml,${encodeURIComponent(STAGE_IMAGE)}`;
    // Decorative: the headline it stands in for arrives with the app, and announcing a logo twice
    // is noise for a screen reader.
    stage.alt = "";
    // The point of this image is to be painted as early as possible, so it must not be lazy and
    // must not wait behind an off-main-thread decode.
    stage.loading = "eager";
    stage.decoding = "sync";
    stage.setAttribute("fetchpriority", "high");

    const ring = document.createElement("div");
    ring.className = `${LOADING_ROOT_CLASS}__ring`;
    ring.setAttribute("role", "status");
    ring.setAttribute("aria-label", "Rechner wird geladen");

    placeholder.appendChild(stage);
    placeholder.appendChild(ring);

    host.appendChild(placeholder);
};

/**
 * Removes the placeholder. Call this *after* the host has been made visible again, in the same
 * synchronous block - swapping in the other order paints a frame in which neither is on the page
 * and costs a second layout shift.
 */
export const stopLoadingScreen = (): void => {
    placeholder?.remove();
    placeholder = null;
};
